import React from 'react';
import { createRoot } from 'react-dom/client';
import * as serviceWorker from './serviceWorker';
import { BrowserRouter, matchPath } from 'react-router-dom';
import './assets/base.scss';
import Main from './main';
import { Provider } from 'react-redux';
import configureAppStore from './store/configureStore';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { routes } from './routes';
import { registerLicense } from '@syncfusion/ej2-base';
import { hideSpecificWarnings } from './dev';
import { isAndroid, isNative } from './capacitor/platform';
import { startKeyboard } from './capacitor/keyboard';
import { setDefaultStatusBarStyle } from './capacitor/statusBar';
import { installAppHttp } from './http/appHttp';
import { connectAccessToken } from './store/actions/authActions/auth';
import { scheduleSplashFallback } from './capacitor/splash';
import { registerOfflineRevalidation } from './store/offlineRevalidation';
import { ResponsiveProvider } from './main/platform/dashboards/components/hooks/useResponsive';
import { FluentProvider } from '@fluentui/react-components';
import { useMaperTheme } from './theme/hooks/useMaperTheme';

hideSpecificWarnings();

if (isNative()) {
    document.body.classList.add('native-app');
    if (isAndroid()) startKeyboard();
}
setDefaultStatusBarStyle();
scheduleSplashFallback();

registerLicense(process.env.REACT_APP_SYNCFUSION_LICENSE_KEY || '');

const store = configureAppStore();
registerOfflineRevalidation(store.dispatch);
connectAccessToken(store.dispatch, store.getState);
installAppHttp();

const rootElement = document.getElementById('root');
const root = createRoot(rootElement);

if (process.env.REACT_APP_ENABLE_SENTRY === 'true') {
    Sentry.init({
        dsn: 'https://ca5324a266d5420998ab8350b8c7e26d@o977311.ingest.sentry.io/5933827',
        environment: process.env.REACT_APP_SENTRY_ENVIRONMENT,
        release: process.env.REACT_APP_VERSION,
        integrations: [
            new Integrations.BrowserTracing({
                routingInstrumentation: Sentry.reactRouterV5Instrumentation(window.history, routes, matchPath),
            }),
        ],
        // The native app serves the bundle from its own origin — capacitor://localhost on iOS,
        // https://localhost on Android — so without those the filter drops every app error:
        // it matches the last stack frame's URL, and an event that matches nothing is discarded.
        allowUrls: [/(?:[a-z]+\.)?mapertech\.com/, /^(?:https|capacitor):\/\/localhost(?:$|\/)/],
        ignoreErrors: ['ResizeObserver loop limit exceeded'],
        tracesSampleRate: 1.0,
    });
}

// FluentThemeWrapper must be outside the Sentry conditional block
const FluentThemeWrapper = ({ children }) => {
    const theme = useMaperTheme();
    return <FluentProvider theme={theme}>{children}</FluentProvider>;
};

const renderApp = (Component) => {
    root.render(
        <Provider store={store}>
            <FluentThemeWrapper>
                <ResponsiveProvider>
                    <BrowserRouter>
                        <Component />
                    </BrowserRouter>
                </ResponsiveProvider>
            </FluentThemeWrapper>
        </Provider>,
    );
};

renderApp(Main);

if (module.hot) {
    module.hot.accept('./main', () => {
        const NextApp = require('./main').default;
        renderApp(NextApp);
    });
}
serviceWorker.unregister();

export default store;
